/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';
import flow from 'lodash/fp/flow';
import pluck from 'lodash/fp/pluck';
import filter from 'lodash/fp/filter';
import { View } from 'backbone.marionette';
import Syphon from 'backbone.syphon';

import counties from './counties.json';
import './AddressView.css';
import tmplAddressView from './AddressView.hbs';

const AddressView = View.extend({
  template: tmplAddressView,

  options: {
    horizontal: true,
    objectName: null,
    startingCountry: 'US',
    startingPostalCode: null,
    startingCounty: null,
    isRequired: false,
    requiredFields: {
      addressLine1: false,
    },
  },

  className() {
    const { horizontal } = this.options;
    const className = 'address-view';
    return horizontal ? `${className} form-horizontal` : className;
  },

  templateContext() {
    const { horizontal, objectName, isRequired, requiredFields } = this.options;
    return {
      horizontal,
      objectName,
      isRequired,
      addressLine1Required: isRequired || requiredFields.addressLine1,
    };
  },

  ui: {
    country: '.country',
    stateTextInput: '.state-text-input',
    usStateSelect: '.us-states-select',
    countyContainer: '.county-container',
    addressLine3: '.address-line-3',
    postalCodeFormGroup: '.postal-code-form-group',
    postalCodeInput: '.zip-code-input',
    stateFormGroup: '.state-form-group',
    paCountySelect: '.pa-counties-select',
  },

  events: {
    'change @ui.country': 'onChangeCountry',
    'change @ui.postalCodeInput': 'onChangePostalCode',
  },

  onRender() {
    const { startingCountry, startingPostalCode, startingCounty } = this.options;
    this.hideCountyOptions();
    if (startingCountry && startingCountry !== 'US') {
      this.setInternationalView();
    } else {
      this.setDomesticView();
    }
    if (startingPostalCode) {
      this.setStateAndCounty(startingPostalCode, startingCounty);
    }
  },

  onChangePostalCode() {
    this.setStateAndCounty(this.ui.postalCodeInput.val());
    // clear validation errors on state select after the county has changed.
    if (this.options.isRequired) {
      this.ui.usStateSelect.parsley().reset();
    }
  },

  hideCountyOptions() {
    this.ui.paCountySelect
      .children('option')
      .filter('[value!=""][value!="Out of State"]')
      .wrap('<span>')
      .parent()
      .hide();
  },

  setState(state) {
    this.ui.usStateSelect.val(state);
  },

  setStateAndCounty(postalCode, county) {
    this.hideCountyOptions();

    if (postalCode) {
      const fiveDigitZip = parseInt(postalCode.substring(0, 5));

      const countiesForZip = flow(filter({ zipCode: fiveDigitZip }), pluck('county'))(counties);

      if (countiesForZip.length) {
        for (const county of countiesForZip) {
          const option = this.ui.paCountySelect.find(`option[value="${county}"]`);
          option.parent().replaceWith(option);
        }
        this.ui.usStateSelect.val('PA');
        if (county && _.contains(countiesForZip, county)) {
          this.ui.paCountySelect.val(county);
        } else {
          this.ui.paCountySelect.val(countiesForZip[0]);
        }
      } else {
        this.ui.paCountySelect.val('Out of State');
      }
    } else {
      this.ui.paCountySelect.val('');
    }
  },

  onChangeCountry() {
    const activeCountry = this.ui.country.val();
    if (activeCountry === 'US') {
      this.setDomesticView();
    } else {
      this.setInternationalView();
    }
  },

  setDomesticView() {
    this.ui.countyContainer.show();
    this.ui.stateTextInput.val('').prop('disabled', true).hide();
    this.ui.addressLine3.val('').prop('disabled', true).hide();
    this.ui.usStateSelect.prop('disabled', false).show();
    if (this.options.isRequired) {
      this.ui.postalCodeFormGroup.addClass('required');
      this.ui.stateFormGroup.addClass('required');
      this.ui.postalCodeInput.attr('data-parsley-required', 'true');
    }
    this.ui.postalCodeInput.parsley().reset();
  },

  setInternationalView() {
    this.ui.addressLine3.prop('disabled', false).show();
    this.ui.stateTextInput.prop('disabled', false).show();
    this.ui.countyContainer.hide();
    this.ui.usStateSelect.val('').prop('disabled', true).hide();
    if (this.options.isRequired) {
      this.ui.postalCodeFormGroup.removeClass('required');
      this.ui.stateFormGroup.removeClass('required');
      this.ui.postalCodeInput.removeAttr('data-parsley-required');
    }
    this.ui.postalCodeInput.parsley().reset();
  },

  // Use only when dynamically populating address within a form
  setAddress(address) {
    const { objectName } = this.options;
    if (objectName) {
      const addressObj = {};
      addressObj[objectName] = address;

      Syphon.deserialize(this, addressObj);
      this.setInitialFormState(addressObj[objectName]);
    } else {
      Syphon.deserialize(this, address);
      this.setInitialFormState(address);
    }
  },

  setInitialFormState(address) {
    const { country, postalCode, county } = address;
    this.hideCountyOptions();
    if (country && country !== 'US') {
      this.setInternationalView();
    }
    if (postalCode) {
      this.setStateAndCounty(postalCode, county);
    }
  },
});

export default AddressView;
